import { Badge, Button } from '@/components/UI';
import { isLogicContainerNode, type LogicNode, type TradeLogicDiagnostic, type TradeLogicDraft, type TradeLogicNodeId } from '../model';
import type {
    TradeLogicRegistryActionTemplateDto,
    TradeLogicRegistryConditionTemplateDto,
    TradeLogicRegistryFeatureDto
} from '../registry';
import { formatLogicNodeSummary, type NodeSummaryFormatContext } from './nodeSummaryFormat';

interface DraftTreePanelProps {
    draft: TradeLogicDraft;
    diagnostics: TradeLogicDiagnostic[];
    selectedNodeId: TradeLogicNodeId | null;
    featuresByKey: Record<string, TradeLogicRegistryFeatureDto>;
    actionTemplatesByKey: Record<string, TradeLogicRegistryActionTemplateDto>;
    conditionTemplatesByKey: Record<string, TradeLogicRegistryConditionTemplateDto>;
    onSelectNode: (nodeId: TradeLogicNodeId) => void;
    onToggleNode: (nodeId: TradeLogicNodeId) => void;
    onRemoveNode: (nodeId: TradeLogicNodeId) => void;
    onMoveNode: (nodeId: TradeLogicNodeId, direction: 'up' | 'down') => void;
    onDuplicateNode: (nodeId: TradeLogicNodeId) => void;
    onWrapNode: (nodeId: TradeLogicNodeId, wrapperKind: 'group' | 'sequence') => void;
    onUnwrapNode: (nodeId: TradeLogicNodeId) => void;
}

const DraftTreePanel = ({
    draft,
    diagnostics,
    selectedNodeId,
    featuresByKey,
    actionTemplatesByKey,
    conditionTemplatesByKey,
    onSelectNode,
    onToggleNode,
    onRemoveNode,
    onMoveNode,
    onDuplicateNode,
    onWrapNode,
    onUnwrapNode
}: DraftTreePanelProps) => {
    const summaryContext: NodeSummaryFormatContext = {
        featuresByKey,
        actionTemplatesByKey,
        conditionTemplatesByKey
    };

    const errorCount = diagnostics.filter((diagnostic) => diagnostic.severity === 'error').length;
    const diagnosticsLabel = errorCount > 0 ? `${errorCount} error${errorCount === 1 ? '' : 's'}` : `${diagnostics.length} diagnostics`;

    return (
        <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                    <h5 className="text-base font-semibold text-zinc-100">Rule tree</h5>
                    <p className="mt-1 text-xs text-zinc-500">Click a row to edit it in the inspector below.</p>
                </div>
                <Badge variant={errorCount > 0 ? 'danger' : 'success'}>{diagnosticsLabel}</Badge>
            </div>
            <div className="max-h-[420px] overflow-auto rounded-md border border-zinc-800 bg-black/20 p-3">
                {draft.rootNode ? (
                    <LogicNodeRow
                        depth={0}
                        diagnostics={diagnostics}
                        node={draft.rootNode}
                        selectedNodeId={selectedNodeId}
                        siblingCount={1}
                        siblingIndex={0}
                        summaryContext={summaryContext}
                        onDuplicateNode={onDuplicateNode}
                        onMoveNode={onMoveNode}
                        onRemoveNode={onRemoveNode}
                        onSelectNode={onSelectNode}
                        onToggleNode={onToggleNode}
                        onUnwrapNode={onUnwrapNode}
                        onWrapNode={onWrapNode}
                    />
                ) : (
                    <div className="text-sm text-zinc-500">No rules yet. Use the panels on the left to add a condition or action.</div>
                )}
            </div>
            <DiagnosticsList diagnostics={diagnostics} />
        </div>
    );
};

interface LogicNodeRowProps {
    node: LogicNode;
    selectedNodeId: TradeLogicNodeId | null;
    diagnostics: TradeLogicDiagnostic[];
    depth: number;
    siblingIndex: number;
    siblingCount: number;
    summaryContext: NodeSummaryFormatContext;
    onSelectNode: (nodeId: TradeLogicNodeId) => void;
    onToggleNode: (nodeId: TradeLogicNodeId) => void;
    onRemoveNode: (nodeId: TradeLogicNodeId) => void;
    onMoveNode: (nodeId: TradeLogicNodeId, direction: 'up' | 'down') => void;
    onDuplicateNode: (nodeId: TradeLogicNodeId) => void;
    onWrapNode: (nodeId: TradeLogicNodeId, wrapperKind: 'group' | 'sequence') => void;
    onUnwrapNode: (nodeId: TradeLogicNodeId) => void;
}

const LogicNodeRow = (props: LogicNodeRowProps) => {
    const {
        node,
        selectedNodeId,
        diagnostics,
        depth,
        siblingIndex,
        siblingCount,
        summaryContext,
        onSelectNode,
        onToggleNode,
        onRemoveNode,
        onMoveNode,
        onDuplicateNode,
        onWrapNode,
        onUnwrapNode
    } = props;

    const nodeDiagnostics = diagnostics.filter((diagnostic) => diagnostic.nodeId === node.id);
    const rowState = buildLogicNodeRowState({ depth, node, nodeDiagnostics, selectedNodeId, siblingCount, siblingIndex });
    const summary = formatLogicNodeSummary(node, summaryContext);

    return (
        <div>
            <div className={rowState.cardClassName}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <LogicNodeHeader hasError={rowState.hasError} node={node} summary={summary} onSelectNode={onSelectNode} />
                    <LogicNodeActions
                        canDuplicate={rowState.canDuplicate}
                        canMoveDown={rowState.canMoveDown}
                        canMoveUp={rowState.canMoveUp}
                        canUnwrap={rowState.canUnwrap}
                        node={node}
                        onDuplicateNode={onDuplicateNode}
                        onMoveNode={onMoveNode}
                        onRemoveNode={onRemoveNode}
                        onToggleNode={onToggleNode}
                        onUnwrapNode={onUnwrapNode}
                        onWrapNode={onWrapNode}
                    />
                </div>
                <LogicNodeMetadata node={node} />
                <LogicNodeDiagnostics diagnostics={nodeDiagnostics} />
            </div>
            <LogicNodeChildren
                depth={depth}
                diagnostics={diagnostics}
                node={node}
                selectedNodeId={selectedNodeId}
                summaryContext={summaryContext}
                onDuplicateNode={onDuplicateNode}
                onMoveNode={onMoveNode}
                onRemoveNode={onRemoveNode}
                onSelectNode={onSelectNode}
                onToggleNode={onToggleNode}
                onUnwrapNode={onUnwrapNode}
                onWrapNode={onWrapNode}
            />
        </div>
    );
};

interface LogicNodeRowStateInput {
    node: LogicNode;
    nodeDiagnostics: TradeLogicDiagnostic[];
    selectedNodeId: TradeLogicNodeId | null;
    depth: number;
    siblingIndex: number;
    siblingCount: number;
}

interface LogicNodeRowState {
    cardClassName: string;
    canDuplicate: boolean;
    canMoveDown: boolean;
    canMoveUp: boolean;
    canUnwrap: boolean;
    hasError: boolean;
}

const buildLogicNodeRowState = ({
    node,
    nodeDiagnostics,
    selectedNodeId,
    depth,
    siblingIndex,
    siblingCount
}: LogicNodeRowStateInput): LogicNodeRowState => {
    const hasError = nodeDiagnostics.some((diagnostic) => diagnostic.severity === 'error');
    const canMove = depth > 0 && siblingCount > 1;
    const canMoveUp = canMove && siblingIndex > 0;
    const canMoveDown = canMove && siblingIndex < siblingCount - 1;
    const canUnwrap = isLogicContainerNode(node) && depth > 0 && node.children.length > 0;

    return {
        cardClassName: getNodeCardClassName(selectedNodeId === node.id, hasError),
        canDuplicate: depth > 0,
        canMoveDown,
        canMoveUp,
        canUnwrap,
        hasError
    };
};

const getNodeCardClassName = (isSelected: boolean, hasError: boolean) => {
    if (isSelected) {
        return 'mb-2 rounded border border-p314-blue bg-blue-950/20 px-3 py-2';
    }

    if (hasError) {
        return 'mb-2 rounded border border-red-900/70 bg-red-950/20 px-3 py-2';
    }

    return 'mb-2 rounded border border-zinc-800 bg-zinc-900/60 px-3 py-2';
};

const LogicNodeHeader = ({
    hasError,
    node,
    summary,
    onSelectNode
}: {
    hasError: boolean;
    node: LogicNode;
    summary: string;
    onSelectNode: (nodeId: TradeLogicNodeId) => void;
}) => (
    <button className="min-w-0 text-left" type="button" onClick={() => onSelectNode(node.id)}>
        <div className="flex flex-wrap items-center gap-2">
            <Badge variant="dark">{node.kind}</Badge>
            <span className="text-sm font-medium text-zinc-100">{summary}</span>
            {!node.enabled && <Badge variant="warning">Disabled</Badge>}
            {hasError && <Badge variant="danger">Error</Badge>}
        </div>
    </button>
);

const LogicNodeMetadata = ({ node }: { node: LogicNode }) => {
    if (node.kind === 'reference') {
        return (
            <div className="mt-1 text-xs text-zinc-500">
                {node.referenceKind} {String(node.referenceId)}
                {node.version ? ` v${node.version}` : ''}
            </div>
        );
    }

    return null;
};

const LogicNodeDiagnostics = ({ diagnostics }: { diagnostics: TradeLogicDiagnostic[] }) => {
    if (diagnostics.length === 0) return null;

    return (
        <div className="mt-2 space-y-1">
            {diagnostics.map((diagnostic, index) => (
                <div className="text-xs text-red-200" key={`${diagnostic.id}:${index}`}>
                    {diagnostic.message}
                </div>
            ))}
        </div>
    );
};

interface LogicNodeActionsProps {
    node: LogicNode;
    canDuplicate: boolean;
    canMoveDown: boolean;
    canMoveUp: boolean;
    canUnwrap: boolean;
    onToggleNode: (nodeId: TradeLogicNodeId) => void;
    onRemoveNode: (nodeId: TradeLogicNodeId) => void;
    onMoveNode: (nodeId: TradeLogicNodeId, direction: 'up' | 'down') => void;
    onDuplicateNode: (nodeId: TradeLogicNodeId) => void;
    onWrapNode: (nodeId: TradeLogicNodeId, wrapperKind: 'group' | 'sequence') => void;
    onUnwrapNode: (nodeId: TradeLogicNodeId) => void;
}

const LogicNodeActions = ({
    node,
    canDuplicate,
    canMoveDown,
    canMoveUp,
    canUnwrap,
    onToggleNode,
    onRemoveNode,
    onMoveNode,
    onDuplicateNode,
    onWrapNode,
    onUnwrapNode
}: LogicNodeActionsProps) => {
    const actionTarget = node.label || node.id;
    const accessibleActionTarget = `${actionTarget} node ${node.id}`;
    const toggleAction = node.enabled ? 'Disable' : 'Enable';

    return (
        <div aria-label={`Actions for ${accessibleActionTarget}`} className="flex flex-wrap items-center gap-2" role="group">
            <Button
                aria-label={`${toggleAction} ${accessibleActionTarget}`}
                size="sm"
                title={`${toggleAction} ${actionTarget}`}
                variant="outline-dark"
                onClick={() => onToggleNode(node.id)}
            >
                {toggleAction}
            </Button>
            <Button
                aria-label={`Remove ${accessibleActionTarget}`}
                size="sm"
                title={`Remove ${actionTarget}`}
                variant="destructive"
                onClick={() => onRemoveNode(node.id)}
            >
                Remove
            </Button>
            <details className="relative">
                <summary
                    aria-label={`More actions for ${accessibleActionTarget}`}
                    className="cursor-pointer list-none rounded border border-zinc-700 px-2 py-1 text-xs text-zinc-200 hover:border-zinc-500 hover:text-zinc-50"
                    title={`More actions for ${actionTarget}`}
                >
                    More ▾
                </summary>
                <div className="absolute right-0 z-10 mt-1 flex min-w-[160px] flex-col gap-1 rounded-md border border-zinc-700 bg-zinc-900 p-2 shadow-lg">
                    <Button
                        aria-label={`Move ${accessibleActionTarget} up`}
                        disabled={!canMoveUp}
                        size="sm"
                        title={`Move ${actionTarget} up within its parent`}
                        variant="outline-dark"
                        onClick={() => onMoveNode(node.id, 'up')}
                    >
                        Move Up
                    </Button>
                    <Button
                        aria-label={`Move ${accessibleActionTarget} down`}
                        disabled={!canMoveDown}
                        size="sm"
                        title={`Move ${actionTarget} down within its parent`}
                        variant="outline-dark"
                        onClick={() => onMoveNode(node.id, 'down')}
                    >
                        Move Down
                    </Button>
                    <Button
                        aria-label={`Duplicate ${accessibleActionTarget}`}
                        disabled={!canDuplicate}
                        size="sm"
                        title={`Duplicate ${actionTarget} and its children`}
                        variant="outline-dark"
                        onClick={() => onDuplicateNode(node.id)}
                    >
                        Duplicate
                    </Button>
                    <Button
                        aria-label={`Wrap ${accessibleActionTarget} in group`}
                        size="sm"
                        title={`Wrap ${actionTarget} in a new group`}
                        variant="outline-dark"
                        onClick={() => onWrapNode(node.id, 'group')}
                    >
                        Wrap in Group
                    </Button>
                    <Button
                        aria-label={`Wrap ${accessibleActionTarget} in sequence`}
                        size="sm"
                        title={`Wrap ${actionTarget} in a new sequence`}
                        variant="outline-dark"
                        onClick={() => onWrapNode(node.id, 'sequence')}
                    >
                        Wrap in Sequence
                    </Button>
                    <Button
                        aria-label={`Unwrap ${accessibleActionTarget}`}
                        disabled={!canUnwrap}
                        size="sm"
                        title={`Promote ${actionTarget}'s children into its parent`}
                        variant="outline-dark"
                        onClick={() => onUnwrapNode(node.id)}
                    >
                        Unwrap
                    </Button>
                </div>
            </details>
        </div>
    );
};

interface LogicNodeChildrenProps extends Omit<LogicNodeRowProps, 'siblingCount' | 'siblingIndex'> {
    depth: number;
}

const LogicNodeChildren = ({ node, depth, ...childProps }: LogicNodeChildrenProps) => {
    if (!isLogicContainerNode(node)) {
        return null;
    }

    if (node.children.length === 0) {
        if (depth === 0) {
            return (
                <div className="ml-4 mt-2 rounded border border-dashed border-zinc-700 bg-zinc-950/40 px-3 py-4 text-center text-xs text-zinc-500">
                    No rules yet. Use the panels on the left to add a condition, action, or wrap a group.
                </div>
            );
        }

        return null;
    }

    return (
        <div className="ml-4 border-l border-zinc-800 pl-3">
            {node.children.map((child, childIndex) => (
                <LogicNodeRow
                    {...childProps}
                    depth={depth + 1}
                    key={child.id}
                    node={child}
                    siblingCount={node.children.length}
                    siblingIndex={childIndex}
                />
            ))}
        </div>
    );
};

const DiagnosticsList = ({ diagnostics }: { diagnostics: TradeLogicDiagnostic[] }) => {
    if (diagnostics.length === 0) return null;

    return (
        <div className="mt-3 space-y-2">
            {diagnostics.map((diagnostic, index) => (
                <div
                    className="rounded-md border border-zinc-800 bg-zinc-900/50 p-2 text-xs text-zinc-300"
                    key={`${diagnostic.id}:${index}`}
                >
                    <span className="font-semibold text-zinc-100">{diagnostic.severity.toUpperCase()}</span> {diagnostic.message}
                </div>
            ))}
        </div>
    );
};

export default DraftTreePanel;
